import React, { KeyboardEvent, PointerEvent, ReactNode, useRef, useState } from "react";

interface RepeatOnHoldProps {
  readonly onTrigger: () => void;
  readonly intervalInMs: number;
  readonly children: ReactNode;
}

function isLeftClick(event: PointerEvent<HTMLDivElement>): boolean {
  return event.pointerType !== "mouse" || event.button === 0;
}

function isSpaceBar(event: KeyboardEvent<HTMLDivElement>): boolean {
  return event.key === " ";
}

export function RepeatOnHold({
  onTrigger,
  intervalInMs = 300,
  children,
}: RepeatOnHoldProps): React.JSX.Element {
  const [isHolding, setIsHolding] = useState(false);
  const intervalId = useRef<number>(undefined);

  const clearTimeout = () => {
    if (intervalId.current) {
      clearInterval(intervalId.current);
      intervalId.current = undefined;
    }
  };

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (isLeftClick(event)) {
      intervalId.current = window.setInterval(() => {
        setIsHolding(true);
        onTrigger();
      }, intervalInMs);
    }
  };

  const handlePointerUp = (event: PointerEvent<HTMLDivElement>) => {
    clearTimeout();
    if (!isHolding && isLeftClick(event)) {
      onTrigger();
    }
    setIsHolding(false);
  };

  const handlePointerLeave = () => clearTimeout();
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!isSpaceBar(event)) return;
    onTrigger();
  };

  return (
    <div
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerLeave}
      onKeyDown={handleKeyDown}
    >
      {children}
    </div>
  );
}
